import type { ProjectElementType } from '@/hooks/api/useResourceLibrary';
import { NotificationType, type Notification } from '@/types/notification';

export function getNotificationMetadataString(
  metadata: Record<string, unknown>,
  ...keys: string[]
): string {
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === 'string' && value) return value;
  }
  return '';
}

export function getNotificationMetadataStringArray(
  metadata: Record<string, unknown>,
  ...keys: string[]
): string[] {
  for (const key of keys) {
    const value = metadata[key];
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
    }
  }
  return [];
}

export function isProjectAccessRevokedNotification(type: NotificationType): boolean {
  return type === NotificationType.PROJECT_MEMBER_REMOVED;
}

export function isProjectAuthChangedNotification(type: NotificationType): boolean {
  return (
    type === NotificationType.PROJECT_MEMBER_AUTH_CHANGED ||
    type === NotificationType.PROJECT_MEMBER_GROUPS_CHANGED
  );
}

export function isOrgRemovalLikeNotification(type: NotificationType): boolean {
  return (
    type === NotificationType.ORG_MEMBER_REMOVED ||
    type === NotificationType.ORG_MEMBER_SUSPENDED ||
    type === NotificationType.ORG_MEMBER_LEFT
  );
}

export function isStoryboardReviewNotification(type: NotificationType): boolean {
  return (
    type === NotificationType.STORYBOARD_REVIEW_REQUESTED ||
    type === NotificationType.STORYBOARD_REVIEW_APPROVED ||
    type === NotificationType.STORYBOARD_REVIEW_REJECTED
  );
}

export function getResourceKindFromNotificationMetadata(
  metadata: Record<string, unknown>,
  ...extraKindKeys: string[]
): ProjectElementType | null {
  const kind = getNotificationMetadataString(
    metadata,
    'resourceKind',
    'resource_kind',
    ...extraKindKeys
  );
  return kind === 'character' || kind === 'location' || kind === 'prop' ? kind : null;
}

export type InvitationCloseOutcome = 'cancelled' | 'expired';

export function getInvitationCloseOutcome(
  metadata: Record<string, unknown>
): InvitationCloseOutcome | null {
  const value = getNotificationMetadataString(metadata, 'invitationOutcome', 'invitation_outcome');
  if (value === 'cancelled' || value === 'expired') return value;
  return null;
}

export function invitationCloseLabel(
  outcome: InvitationCloseOutcome | null,
  notificationType?: NotificationType
): string {
  if (outcome === 'cancelled') return '邀请已撤回';
  if (outcome === 'expired') {
    return notificationType === NotificationType.PROJECT_ORG_COLLAB_INVITED
      ? '邀请已过期'
      : '邀请已失效';
  }
  return '';
}

export function isInvitationCloseNotice(
  notification: Pick<Notification, 'type' | 'actionTaken' | 'metadata'>
): boolean {
  if (!notification.actionTaken) return false;
  if (
    notification.type !== NotificationType.ORG_INVITATION_RECEIVED &&
    notification.type !== NotificationType.PROJECT_ORG_COLLAB_INVITED
  ) {
    return false;
  }
  return getInvitationCloseOutcome(notification.metadata) !== null;
}

export function isExpiredInvitationError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : '';
  return message.includes('已过期') || message.includes('已失效');
}

export function getProjectIdsFromNotificationMetadata(notification: Notification): Set<string> {
  return new Set(
    [
      getNotificationMetadataString(notification.metadata, 'projectId', 'project_id'),
      ...getNotificationMetadataStringArray(
        notification.metadata,
        'affectedProjectIds',
        'affected_project_ids'
      ),
    ].filter((id): id is string => Boolean(id))
  );
}
